import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import AFSService from "../../../../../../services/AFSService";
import "./checklist-panel.css";
import WComponent from "../../../../../common/WComponent";

const STATUS_META = {
	complete: { label: "Complete", icon: Icon.CheckCircle, modifier: "complete", color: "#4caf50" },
	blocked: { label: "Blocked", icon: Icon.AlertOctagon, modifier: "blocked", color: "#c62828" },
	not_started: { label: "Not started", icon: Icon.Circle, modifier: "not-started", color: "#bdbdbd" },
	waiting_for_action: { label: "Action required", icon: Icon.Clock, modifier: "waiting-for-action", color: "#e6a117" },
};

function StatusIcon({ status }) {
	let meta = STATUS_META[status] || STATUS_META.not_started;
	let Glyph = meta.icon;
	return <Glyph size={14} color={meta.color} className="afs-compliance__glyph" />;
}

const ACTION_LABEL = {
	request_input: "Provide input",
	request_review: "Review now",
	generate_document: "Generate document",
	propose_aje: "Propose AJE",
	acknowledge: "Acknowledge",
	manual_acknowledgment: "Acknowledge",
	resolve_upstream: "View failing check",
};

// Maps the server-side `target` vocabulary used on request_review / generate_document
// actions to the tab IDs this component can navigate to (see AFSTool/shared/helpers/constants.js).
// A target that isn't in this map falls back to rendering a disabled button — no
// silent "(coming soon)" dead-ends.
const TARGET_TO_TAB = {
	source_files: "bookkeeping",
	material_accounts: "tb",
	variances: "review",
	estimates: "review",
	approvals: "review",
	compilation_report: "report",
};

function endsWithAt(name) { return typeof name === "string" && name.length > 3 && name.lastIndexOf("_at") === name.length - 3; }

function EvidenceRow({ evidence }) {
	let Glyph = evidence.present ? Icon.Check : Icon.X;
	return (
		<div className={clsx("afs-compliance__evidence-row", evidence.present && "afs-compliance__evidence-row--present")}>
			<Glyph size={12} />
			<span className="afs-compliance__evidence-desc">{evidence.description}</span>
			{!evidence.present && (
				<span className="afs-compliance__evidence-tag">Missing</span>
			)}
		</div>
	);
}

function fmtFileSize(size) {
	if (!size) return "";
	if (size > 1048576) return (size / 1048576).toFixed(1) + " MB";
	return Math.round(size / 1024) + " KB";
}

function EvidenceDocumentRow({ evidence, isPending, onUpload, onDelete }) {
	let doc = evidence.document;
	let hasDoc = !!doc;
	let Glyph = evidence.present ? Icon.Check : Icon.X;

	function handleFile(e) {
		let file = e.target.files && e.target.files[0];
		if (!file) return;
		onUpload(file);
		if (e.target) e.target.value = "";
	}

	return (
		<div className={clsx("afs-compliance__evidence-row", evidence.present && "afs-compliance__evidence-row--present")}>
			<Glyph size={12} />
			<span className="afs-compliance__evidence-desc">{evidence.description}</span>
			{hasDoc ? (
				<span className="afs-compliance__evidence-doc">
					<Icon.FileText size={12} color="#2e7d32" />
					<span className="afs-compliance__evidence-doc-name" title={doc.name + "." + doc.extension}>
						{doc.name}.{doc.extension}
					</span>
					<span className="afs-compliance__evidence-doc-size">{fmtFileSize(doc.size)}</span>
					<button
						className="afs-compliance__evidence-doc-btn"
						onClick={onDelete}
						disabled={isPending}
						title="Remove document"
					>
						<Icon.Trash2 size={11} />
					</button>
				</span>
			) : (
				<label className={clsx("afs-compliance__evidence-upload", isPending && "afs-compliance__evidence-upload--disabled")}>
					<Icon.Upload size={11} /> {isPending ? "Uploading…" : "Upload"}
					<input type="file" className="afs-compliance__file-input-hidden" onChange={handleFile} disabled={isPending} />
				</label>
			)}
		</div>
	);
}

function SectionEvidenceCard({ section }) {
	let total = section.missing.length + section.satisfied.length + section.waived.length;
	let allComplete = section.missing.length === 0 && total > 0;
	return (
		<div className={clsx("afs-compliance__section", allComplete && "afs-compliance__section--complete")}>
			<div className="afs-compliance__section-head afs-compliance__section-head--static">
				<span className="afs-compliance__section-title">
					{section.wpRef} — {section.title || section.wpRef}
				</span>
				<span className={clsx("afs-compliance__section-count", allComplete && "afs-compliance__section-count--complete")}>
					{section.satisfied.length}/{section.satisfied.length + section.missing.length} required
					{allComplete && <Icon.Check size={11} />}
				</span>
			</div>
			{(section.missing.length > 0 || section.waived.length > 0) && (
				<div className="afs-compliance__section-body">
					{section.missing.map(function (m, i) {
						return (
							<div key={"m" + i} className="afs-compliance__evidence-row">
								<Icon.X size={12} />
								<span className="afs-compliance__evidence-desc">{m.label}</span>
								<span className="afs-compliance__evidence-tag">Missing</span>
							</div>
						);
					})}
					{section.waived.map(function (w, i) {
						return (
							<div key={"w" + i} className="afs-compliance__evidence-row" title={w.justification || ""}>
								<Icon.MinusCircle size={12} color="#856404" />
								<span className="afs-compliance__evidence-desc">{w.label}</span>
								<span className="afs-compliance__evidence-tag afs-compliance__evidence-tag--waived">Waived</span>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

function BlockedDetails({ linkedChecks }) {
	let failed = linkedChecks.filter(function (c) { return c.passed === false; });
	if (failed.length === 0) return null;
	return (
		<div className="afs-compliance__ci-section">
			<div className="afs-compliance__ci-section-label">Why blocked</div>
			<div className="afs-compliance__blocked-details">
				{failed.map(function (c) {
					return (
						<div key={c.id} className="afs-compliance__blocked-detail">
							<span className="afs-compliance__blocked-detail-id">{c.id}</span>
							<span className="afs-compliance__blocked-detail-text">{c.detail || c.label}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function CompleteDetails({ linkedChecks }) {
	// Only show real (non-placeholder) checks that passed — those are the ones
	// actually proving the requirement. Placeholder passes are noise here.
	let passed = linkedChecks.filter(function (c) {
		return !c.is_placeholder && (c.passed === true || c.status === "not_applicable");
	});
	if (passed.length === 0) return null;
	return (
		<div className="afs-compliance__ci-section">
			<div className="afs-compliance__ci-section-label">Why complete</div>
			<div className="afs-compliance__passed-details">
				{passed.map(function (c) {
					return (
						<div key={c.id} className="afs-compliance__passed-detail">
							<span className="afs-compliance__passed-detail-id">{c.id}</span>
							<span className="afs-compliance__passed-detail-text">{c.passed_label || c.label}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function formatAttestationDate(iso) {
	if (!iso) return "";
	let d = new Date(iso);
	if (isNaN(d.getTime())) return iso;
	return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function AttestationCard({ attestation, manualLabel }) {
	let when = attestation ? formatAttestationDate(attestation.acknowledged_at) : "";
	let who = attestation && attestation.acknowledged_by_name ? attestation.acknowledged_by_name : null;
	let note = attestation ? attestation.note : null;
	return (
		<div className="afs-compliance__attestation">
			<div className="afs-compliance__attestation-head">
				<Icon.CheckCircle size={12} />
				<span className="afs-compliance__attestation-title">{manualLabel || "Signed off"}</span>
			</div>
			<div className="afs-compliance__attestation-body">
				<div className="afs-compliance__attestation-meta">
					{who ? <>Signed by <strong>{who}</strong></> : <em>Legacy sign-off — no signer metadata recorded</em>}
					{when && <> on {when}</>}
				</div>
				{note && (
					<div className="afs-compliance__attestation-note">{note}</div>
				)}
			</div>
		</div>
	);
}

// AckForm renders the practitioner sign-off affordance. It has two modes:
//
//   mode="primary"   — used when sign-off is the only authoring path for this
//                      CI (no request_input etc.). Button is full-width with
//                      the registry label and a supporting hint.
//
//   mode="secondary" — used when a more specific "Next step" action exists
//                      and sign-off is the fallback. Renders as a small inline
//                      link ("Or sign off manually →") that expands the note
//                      form on click. Keeps both authoring paths available
//                      without giving them equal visual weight (avoids the
//                      two-peer-buttons oscillation).
class AckForm extends WComponent {
	state = {
		open: false,
		note: "",
	};

	submit = (e) => {
		let { ciId, onSubmit } = this.props;
		let { note } = this.state;
		if (e && e.preventDefault) e.preventDefault();
		onSubmit(ciId, note.trim() || null);
	};

	render() {
	let { label, isPending, mode } = this.props;
	let { open, note } = this.state;

	let resolvedLabel = label || "Sign off";
	let isSecondary = mode === "secondary";

	if (!open) {
		if (isSecondary) {
			return (
				<button
					type="button"
					className="afs-compliance__ack-secondary-link"
					onClick={() => { this.update({ open: true }); }}
				>
					Or sign off manually →
				</button>
			);
		}
		return (
			<div className="afs-compliance__ci-action">
				<button
					className="afs-compliance__action-btn afs-compliance__action-btn--primary"
					onClick={() => { this.update({ open: true }); }}
				>
					{resolvedLabel}
				</button>
				<span className="afs-compliance__action-hint">
					Your name, the time, and your note are recorded as a sign-off on this engagement.
				</span>
			</div>
		);
	}

	return (
		<form className="afs-compliance__ack-form" onSubmit={this.submit}>
			<label className="afs-compliance__ack-form-label">
				{isSecondary ? "Sign off on: " : ""}{resolvedLabel}
			</label>
			<textarea
				className="afs-compliance__ack-form-note"
				value={note}
				onChange={(e) => { this.update({ note: e.target.value }); }}
				placeholder="Note for the working-paper file (optional)"
				rows={3}
				autoFocus
			/>
			<div className="afs-compliance__ack-form-actions">
				<button type="submit" className="afs-compliance__action-btn afs-compliance__action-btn--primary" disabled={isPending}>
					{isPending ? "Signing off…" : "Sign off"}
				</button>
				<button type="button" className="afs-compliance__action-btn afs-compliance__action-btn--ghost" onClick={() => { this.update({ open: false, note: "" }); }} disabled={isPending}>
					Cancel
				</button>
			</div>
		</form>
	);
	}
}

class InlineEvidenceForm extends WComponent {
	state = {
		open: false,
		value: "",
	};

	submit = (e, overrideValue) => {
		let { ciId, action, onSubmit } = this.props;
		let { value } = this.state;
		if (e && e.preventDefault) e.preventDefault();
		let submitValue = overrideValue !== undefined ? overrideValue : value;
		if (typeof submitValue === "string") {
			let trimmed = submitValue.trim();
			if (!trimmed) return;
			submitValue = trimmed;
		}
		onSubmit({ ciId: ciId, storage_key: action.field, value: submitValue });
	};

	render() {
	let { action, isPending } = this.props;
	let { open, value } = this.state;
	let hasOptions = Array.isArray(action.options) && action.options.length > 0;
	let isTimestampField = endsWithAt(action.field);

	// Timestamp-shaped field: a single-click "Confirm now" that writes the
	// current timestamp. Opening a form would be busywork — the practitioner's
	// signal is simply "it happened, record it."
	if (isTimestampField) {
		return (
			<div className="afs-compliance__ci-action">
				{action.prompt && <span className="afs-compliance__action-hint">{action.prompt}</span>}
				<button
					className="afs-compliance__action-btn afs-compliance__action-btn--primary"
					disabled={isPending}
					onClick={(e) => { this.submit(e, true); }}
				>
					{isPending ? "Saving…" : "Confirm now"}
				</button>
			</div>
		);
	}

	if (!open) {
		return (
			<div className="afs-compliance__ci-action">
				{action.prompt && <span className="afs-compliance__action-hint">{action.prompt}</span>}
				<button
					className="afs-compliance__action-btn afs-compliance__action-btn--primary"
					onClick={() => { this.update({ open: true }); }}
				>
					Provide input
				</button>
			</div>
		);
	}

	return (
		<form className="afs-compliance__evidence-form" onSubmit={this.submit}>
			{action.prompt && <label className="afs-compliance__evidence-form-label">{action.prompt}</label>}
			{hasOptions ? (
				<select
					className="afs-compliance__evidence-form-select"
					value={value}
					onChange={(e) => { this.update({ value: e.target.value }); }}
					autoFocus
				>
					<option value="">Select…</option>
					{action.options.map(function (opt) {
						return <option key={opt} value={opt}>{opt}</option>;
					})}
				</select>
			) : (
				<textarea
					className="afs-compliance__evidence-form-note"
					value={value}
					onChange={(e) => { this.update({ value: e.target.value }); }}
					rows={3}
					autoFocus
				/>
			)}
			<div className="afs-compliance__evidence-form-actions">
				<button type="submit" className="afs-compliance__action-btn afs-compliance__action-btn--primary" disabled={isPending || !value}>
					{isPending ? "Saving…" : "Record"}
				</button>
				<button type="button" className="afs-compliance__action-btn afs-compliance__action-btn--ghost" onClick={() => { this.update({ open: false, value: "" }); }} disabled={isPending}>
					Cancel
				</button>
			</div>
		</form>
	);
	}
}

function DeepLinkAction({ action, label, tabId, checkId, onNavigate, prompt }) {
	let handleClick = function () {
		if (tabId) onNavigate({ tabId: tabId, checkId: checkId || null });
	};
	// A target we don't know how to resolve shows as disabled rather than
	// silently navigating somewhere wrong. This is the plan's "no dead-end"
	// rule read in reverse: if we can't honor the action, show it clearly.
	let disabled = !tabId;
	return (
		<div className="afs-compliance__ci-action">
			{prompt && <span className="afs-compliance__action-hint">{prompt}</span>}
			<button
				className="afs-compliance__action-btn afs-compliance__action-btn--primary"
				onClick={handleClick}
				disabled={disabled}
				title={disabled ? "No view is wired for this target yet" : undefined}
			>
				{label}
			</button>
		</div>
	);
}

function renderNextAction({ instruction, onNavigate, onSubmitEvidence, isPending }) {
	let action = instruction.next_action;
	if (!action) return null;
	// manual_acknowledgment (generic fallback) and the registry-declared
	// `acknowledge` action both flow through the Sign-an-attestation section
	// rendered separately above; skip them here to avoid duplicate buttons.
	if (action.type === "manual_acknowledgment" || action.type === "acknowledge") return null;

	let label = action.label || ACTION_LABEL[action.type] || "Resolve";

	if (action.type === "request_input" && action.field) {
		return <InlineEvidenceForm ciId={instruction.id} action={action} isPending={isPending} onSubmit={onSubmitEvidence} />;
	}

	if (action.type === "request_review" || action.type === "generate_document") {
		let tabId = TARGET_TO_TAB[action.target] || null;
		return <DeepLinkAction action={action} label={label} tabId={tabId} onNavigate={onNavigate} prompt={action.prompt} />;
	}

	if (action.type === "propose_aje") {
		// Land the practitioner on the Review tab at the first failing check
		// so the inline AJE Scratch Pad is already in view.
		let failing = instruction.linked_checks && instruction.linked_checks.find(function (c) { return c.passed === false; });
		let checkId = failing ? failing.id : null;
		return <DeepLinkAction action={action} label={label} tabId="review" checkId={checkId} onNavigate={onNavigate} prompt={action.prompt} />;
	}

	if (action.type === "resolve_upstream") {
		let failing = instruction.linked_checks && instruction.linked_checks.find(function (c) { return c.passed === false; });
		let checkId = failing ? failing.id : null;
		return <DeepLinkAction action={action} label={label} tabId="review" checkId={checkId} onNavigate={onNavigate} prompt={action.prompt} />;
	}

	// Unknown action type — show disabled rather than silently mis-routing.
	return (
		<div className="afs-compliance__ci-action">
			<button className="afs-compliance__action-btn" disabled title={"Action type '" + action.type + "' is not wired"}>
				{label}
			</button>
		</div>
	);
}

function InstructionRow({ instruction, expanded, onToggle, onAcknowledge, onUnacknowledge, onNavigate, onSubmitEvidence, onUploadDocument, onDeleteDocument, pendingCiId, pendingDocKey }) {
	let nextAction = instruction.next_action;
	let isComplete = instruction.status === "complete";
	let canAcknowledge = instruction.manual_acknowledgment_allowed;
	let isAcknowledged = instruction.acknowledged;
	let isPending = pendingCiId === instruction.id;
	let manualLabel = (instruction.manual_acknowledgment && instruction.manual_acknowledgment.label) || "Sign off";

	// Action-type classification. A "specific" next action is anything other
	// than the generic acknowledgment fallback — those flow through the
	// dedicated sign-off section instead of the Next-step slot.
	let hasSpecificNextAction = !!nextAction && nextAction.type !== "manual_acknowledgment" && nextAction.type !== "acknowledge";
	let showPrimarySignOff = canAcknowledge && !isAcknowledged && !isComplete && !hasSpecificNextAction;
	let showSecondarySignOff = canAcknowledge && !isAcknowledged && !isComplete && hasSpecificNextAction;

	// The Evidence section duplicates information when the CI is incomplete,
	// all its evidence items are unpopulated, and a request_input Next step
	// below will populate them. In that case the practitioner sees the same
	// storage_key named twice — once as a "Missing" row, once as the form
	// prompt. Show Evidence only when it carries signal the Next step doesn't:
	// at least one item already populated (shows proof path), the CI is
	// complete (shows what earned the pass), or the Next step isn't the
	// thing that would fill it.
	let evidenceItems = instruction.evidence || [];
	let anyEvidencePresent = evidenceItems.some(function (e) { return e.present === true; });
	let nextActionPopulatesEvidence = nextAction && nextAction.type === "request_input" && nextAction.field
		&& evidenceItems.some(function (e) { return e.key === nextAction.field; });
	let showEvidenceSection = evidenceItems.length > 0 && (isComplete || anyEvidencePresent || !nextActionPopulatesEvidence);

	return (
		<div className={clsx("afs-compliance__ci", "afs-compliance__ci--" + instruction.status)}>
			<button className="afs-compliance__ci-head" onClick={onToggle}>
				<span className="afs-compliance__ci-caret">
					{expanded ? <Icon.ChevronDown size={12} /> : <Icon.ChevronRight size={12} />}
				</span>
				<StatusIcon status={instruction.status} />
				<span className="afs-compliance__ci-label">{instruction.checklist_item}</span>
			</button>

			{expanded && (
				<div className="afs-compliance__ci-body">
					{instruction.status === "blocked" && <BlockedDetails linkedChecks={instruction.linked_checks} />}
					{instruction.status === "complete" && <CompleteDetails linkedChecks={instruction.linked_checks} />}

					{/* Evidence — shows what earned the pass (complete CIs) or what
					    still needs filling in (incomplete CIs with multiple or
					    indirect evidence paths). Hidden when the Next step below
					    is itself the thing that would populate the only missing
					    evidence row — no point telling the practitioner the same
					    storage_key twice. */}
					{showEvidenceSection && (
						<div className="afs-compliance__ci-section">
							<div className="afs-compliance__ci-section-label">Evidence</div>
							<div className="afs-compliance__ci-evidence">
								{evidenceItems.map(function (e) {
									if (e.evidence_type === "document_ref") {
										return (
											<EvidenceDocumentRow
												key={e.key}
												evidence={e}
												isPending={pendingDocKey === e.key}
												onUpload={function (file) { onUploadDocument({ storage_key: e.key, file: file }); }}
												onDelete={function () { onDeleteDocument({ storage_key: e.key }); }}
											/>
										);
									}
									return <EvidenceRow key={e.key} evidence={e} />;
								})}
							</div>
						</div>
					)}

					{/* The server-side "no automated check wired" notice is accurate
					    for Category 2 CIs in later phases, but misleading in Phase A
					    when specific authoring paths are already rendered below. Only
					    surface it when no other affordance is shown. */}
					{instruction.notice && !hasSpecificNextAction && !canAcknowledge && (
						<div className="afs-compliance__ci-note">{instruction.notice.message}</div>
					)}

					{/* Primary action: the specific Next-step slot. When present, it
					    carries the CI forward via a real authored artifact (evidence
					    write, deep-link into the view where authorship happens, etc.),
					    and the optional sign-off below becomes a subordinate link. */}
					{!isComplete && !isAcknowledged && hasSpecificNextAction && (
						<div className="afs-compliance__ci-section">
							<div className="afs-compliance__ci-section-label">Next step</div>
							{renderNextAction({
								instruction: instruction,
								onNavigate: onNavigate,
								onSubmitEvidence: onSubmitEvidence,
								isPending: isPending,
							})}
							{showSecondarySignOff && (
								<AckForm
									ciId={instruction.id}
									label={manualLabel}
									onSubmit={onAcknowledge}
									isPending={isPending}
									mode="secondary"
								/>
							)}
						</div>
					)}

					{/* Sign-off as primary: CIs where the registry declares no
					    specific action (auto_resolve was struck, or the CI is
					    inherently sign-only). The button carries the registry's
					    practitioner-friendly label. */}
					{showPrimarySignOff && (
						<div className="afs-compliance__ci-section">
							<div className="afs-compliance__ci-section-label">Sign-off</div>
							<AckForm
								ciId={instruction.id}
								label={manualLabel}
								onSubmit={onAcknowledge}
								isPending={isPending}
								mode="primary"
							/>
						</div>
					)}

					{/* Post-sign-off: show the signed card + remove action. */}
					{canAcknowledge && isAcknowledged && (
						<div className="afs-compliance__ci-section">
							<div className="afs-compliance__ci-section-label">Sign-off</div>
							<AttestationCard attestation={instruction.acknowledgment} manualLabel={manualLabel} />
							<div className="afs-compliance__ci-action">
								<button
									className="afs-compliance__action-btn afs-compliance__action-btn--ghost"
									disabled={isPending}
									onClick={function () { onUnacknowledge(instruction.id); }}
								>
									{isPending ? "Removing…" : "Remove sign-off"}
								</button>
								<span className="afs-compliance__action-hint">
									Removed sign-offs stay in the working-paper file's audit trail.
								</span>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

function SectionCard({ section, expandedIds, onToggleInstruction, open, onToggleSection, onAcknowledge, onUnacknowledge, onNavigate, onSubmitEvidence, onUploadDocument, onDeleteDocument, pendingCiId, pendingDocKey }) {
	let allComplete = section.complete === section.total;
	return (
		<div className={clsx("afs-compliance__section", allComplete && "afs-compliance__section--complete")}>
			<button className="afs-compliance__section-head" onClick={onToggleSection}>
				<span className="afs-compliance__section-caret">
					{open ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
				</span>
				<span className="afs-compliance__section-title">{section.section_label}</span>
				<span className="afs-compliance__section-ref">{section.csrs_section}</span>
				<span className={clsx("afs-compliance__section-count", allComplete && "afs-compliance__section-count--complete")}>
					{section.complete}/{section.total}
					{allComplete && <Icon.Check size={11} />}
				</span>
			</button>

			{open && (
				<div className="afs-compliance__section-body">
					{section.instructions.map(function (ci) {
						return (
							<InstructionRow
								key={ci.id}
								instruction={ci}
								expanded={expandedIds.has(ci.id)}
								onToggle={function () { onToggleInstruction(ci.id); }}
								onAcknowledge={onAcknowledge}
								onUnacknowledge={onUnacknowledge}
								onNavigate={onNavigate}
								onSubmitEvidence={onSubmitEvidence}
								onUploadDocument={onUploadDocument}
								onDeleteDocument={onDeleteDocument}
								pendingCiId={pendingCiId}
								pendingDocKey={pendingDocKey}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}

class ChecklistPanel extends WComponent {
	state = {
		loading: false,
		error: null,
		data: null,
		expandedInstructions: new Set(),
		openSections: new Set(),
		pendingCiId: null,
		pendingDocKey: null,
	};

	loadChecklist = () => {
		let { clientId } = this.props;
		if (!clientId) return Promise.resolve(null);
		return AFSService.getComplianceChecklist({ clientId }).then((result) => {
			if (!result || !result.instructions) {
				this.update({ error: "Failed to load compliance checklist" });
				return null;
			}
			this.update({ data: result });
			return result;
		});
	};

	loadInitialChecklist = () => {
		let { clientId } = this.props;
		if (!clientId) return;
		this._loadCancelled = false;
		this.update({ loading: true });
		this.update({ error: null });
		AFSService.getComplianceChecklist({ clientId }).then((result) => {
			if (this._loadCancelled) return;
			if (!result || !result.instructions) {
				this.update({ error: "Failed to load compliance checklist" });
				this.update({ loading: false });
				return;
			}
			this.update({ data: result });
			let defaultOpen = new Set();
			(result.sections || []).forEach(function (s) {
				if (s.complete < s.total) defaultOpen.add(s.section_label);
			});
			this.update({ openSections: defaultOpen });
			this.update({ loading: false });
		});
	};

	componentDidMount() {
		this.loadInitialChecklist();
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.clientId !== prevProps.clientId) {
			this._loadCancelled = true;
			this.loadInitialChecklist();
		}
	}

	componentWillUnmount() {
		this._loadCancelled = true;
	}

	setAcknowledgment = (ciId, acknowledge, note) => {
		let { clientId } = this.props;
		this.update({ pendingCiId: ciId });
		AFSService.acknowledgeCi({ clientId, ciId, acknowledge, note }).then((result) => {
			if (!result || result.message) {
				this.update({ error: result && result.message ? result.message : "Failed to update acknowledgment" });
				this.update({ pendingCiId: null });
				return;
			}
			this.loadChecklist().then(() => { this.update({ pendingCiId: null }); });
		}).catch(() => { this.update({ pendingCiId: null }); });
	};

	onAcknowledge = (ciId, note) => { this.setAcknowledgment(ciId, true, note); };
	onUnacknowledge = (ciId) => { this.setAcknowledgment(ciId, false, null); };

	onSubmitEvidence = ({ ciId, storage_key, value }) => {
		let { clientId } = this.props;
		this.update({ pendingCiId: ciId });
		AFSService.setComplianceEvidence({ clientId, storage_key, value }).then((result) => {
			if (!result || result.message) {
				this.update({ error: result && result.message ? result.message : "Failed to record evidence" });
				this.update({ pendingCiId: null });
				return;
			}
			this.loadChecklist().then(() => { this.update({ pendingCiId: null }); });
		}).catch(() => { this.update({ pendingCiId: null }); });
	};

	onUploadDocument = ({ storage_key, file }) => {
		let { clientId } = this.props;
		this.update({ pendingDocKey: storage_key });
		AFSService.uploadComplianceDocument({ clientId, storage_key, file }).then((result) => {
			if (!result || result.message) {
				this.update({ error: result && result.message ? result.message : "Failed to upload document" });
				this.update({ pendingDocKey: null });
				return;
			}
			this.loadChecklist().then(() => { this.update({ pendingDocKey: null }); });
		}).catch(() => { this.update({ pendingDocKey: null }); });
	};

	onDeleteDocument = ({ storage_key }) => {
		let { clientId } = this.props;
		this.update({ pendingDocKey: storage_key });
		AFSService.deleteComplianceDocument({ clientId, storage_key }).then((result) => {
			if (!result || result.message) {
				this.update({ error: result && result.message ? result.message : "Failed to remove document" });
				this.update({ pendingDocKey: null });
				return;
			}
			this.loadChecklist().then(() => { this.update({ pendingDocKey: null }); });
		}).catch(() => { this.update({ pendingDocKey: null }); });
	};

	handleNavigate = (args) => {
		let { onNavigate } = this.props;
		if (onNavigate) onNavigate(args);
	};

	toggleInstruction = (id) => {
		let { expandedInstructions } = this.state;
		let next = new Set(expandedInstructions);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		this.update({ expandedInstructions: next });
	};

	toggleSection = (label) => {
		let { openSections } = this.state;
		let next = new Set(openSections);
		if (next.has(label)) next.delete(label);
		else next.add(label);
		this.update({ openSections: next });
	};

	render() {
	let { loading, error, data, expandedInstructions, openSections, pendingCiId, pendingDocKey } = this.state;

	if (loading) {
		return <div className="afs-compliance__loading"><Icon.Loader size={14} /> Loading compliance checklist…</div>;
	}

	if (error) {
		return <div className="afs-compliance__error"><Icon.AlertTriangle size={14} /> {error}</div>;
	}

	if (!data) {
		return <div className="afs-compliance__empty">No compliance data available.</div>;
	}

	let { rollup, gate, sections } = data;

	return (
		<div className="afs-compliance">
			<div className="afs-compliance__header">
				<div className="afs-compliance__header-title">
					<Icon.Shield size={14} />
					CSRS 4200 Compliance
				</div>
				<div className="afs-compliance__header-summary">
					<span className="afs-compliance__summary-stat">{rollup.complete} of {gate.total} complete</span>
					{rollup.blocked > 0 && <span className="afs-compliance__summary-stat afs-compliance__summary-stat--blocked">· {rollup.blocked} blocked</span>}
					{rollup.waiting_for_action > 0 && <span className="afs-compliance__summary-stat afs-compliance__summary-stat--action">· {rollup.waiting_for_action} action required</span>}
				</div>
			</div>

			<div className={clsx("afs-compliance__gate", gate.ready_to_finalize && "afs-compliance__gate--ready")}>
				<Icon.Lock size={12} />
				<span className="afs-compliance__gate-label">
					Finalization gate:&nbsp;
					<strong>{gate.ready_to_finalize ? "READY" : "NOT READY"}</strong>
					&nbsp;— {gate.complete} of {gate.total} complete
				</span>
				<span className="afs-compliance__gate-note">(evaluate-only in v1)</span>
			</div>

			<div className="afs-compliance__sections">
				{sections.map((section) => {
					return (
						<SectionCard
							key={section.section_label}
							section={section}
							expandedIds={expandedInstructions}
							onToggleInstruction={this.toggleInstruction}
							open={openSections.has(section.section_label)}
							onToggleSection={() => { this.toggleSection(section.section_label); }}
							onAcknowledge={this.onAcknowledge}
							onUnacknowledge={this.onUnacknowledge}
							onNavigate={this.handleNavigate}
							onSubmitEvidence={this.onSubmitEvidence}
							onUploadDocument={this.onUploadDocument}
							onDeleteDocument={this.onDeleteDocument}
							pendingCiId={pendingCiId}
							pendingDocKey={pendingDocKey}
						/>
					);
				})}
			</div>

			{Array.isArray(data.section_evidence) && data.section_evidence.length > 0 && (
				<div className="afs-compliance__sections">
					<div className="afs-compliance__header afs-compliance__header--spaced">
						<div className="afs-compliance__header-title">
							<Icon.Paperclip size={14} />
							Section Evidence (Lead Sheet Documentation)
						</div>
					</div>
					{data.section_evidence.map(function (s) {
						return <SectionEvidenceCard key={s.wpRef} section={s} />;
					})}
				</div>
			)}
		</div>
	);
	}
}

export default ChecklistPanel;
